import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployees } from "../../redux/employer-employeeSlice/EmployerEmployeeSlice";
import { toast } from "sonner";
import axios from "axios";
import PaystackPop from "@paystack/inline-js";

const JobSeekerBenefits = () => {
  const dispatch = useDispatch();
  const { employees, status, error } = useSelector((state) => state.employerEmployee || {});
  const { currentUser } = useSelector((state) => state.user || {});
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch employee data and benefits
  useEffect(() => {
    if (!currentUser?._id) {
      toast.error("User not authenticated");
      return;
    }

    // Fetch employee data
    dispatch(fetchEmployees({ id: currentUser._id, userType: "jobSeeker" }))
      .unwrap()
      .then((data) => {
        const employeeData = Array.isArray(data) ? data.find((emp) => emp._id === currentUser._id) : data;
        if (employeeData && employeeData._id) {
          setEmployee(employeeData);
        } else {
          toast.error("No employee data found");
        }
      })
      .catch((err) => {
        toast.error("Failed to load employee data");
      });

    // Fetch benefits
    const fetchBenefits = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}benefit/getUserBenefit`,
          { applicantId: currentUser._id },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setEmployee((prev) => ({ ...prev, benefits: response.data.data }));
      } catch (err) {
        toast.error("Failed to load benefits");
      }
    };
    fetchBenefits();
  }, [currentUser?._id, dispatch]);

  const handlePayBenefit = async (amount, type, period) => {
    if (!employee) {
      toast.error("No employee data available");
      return;
    }

    setLoading(true);
    const paystack = new PaystackPop();
    paystack.newTransaction({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: currentUser.email,
      amount: Math.round(parseFloat(amount) * 100), 
      metadata: {
        applicantId: currentUser._id,
        benefitType: type,
        period,
      },
      onSuccess: async (transaction) => {
        try {
          // Verify payment with Paystack
          const paystackResponse = await axios.get(
            `https://api.paystack.co/transaction/verify/${transaction.reference}`,
            {
              headers: {
                Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_SECRET_KEY}`,
              },
            }
          );

          if (paystackResponse.data.data.status !== "success") {
            throw new Error("Payment verification failed");
          }

          // Create benefit record
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}benefit/create`,
            {
              applicantId: currentUser._id,
              benefitType: type,
              benefitDescription: `${type} benefit for ${period}`,
              amountRequested: amount.toString(),
              benefitBalance: "0",
              benefitStatus: "Pending",
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          // Update job seeker's benefits array
          await axios.patch(
            `${process.env.REACT_APP_API_URL}jobseeker/updateJobSeeker/${currentUser._id}`,
            { benefits: [...(employee.benefits || []), response.data.data._id] },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          setEmployee((prev) => ({
            ...prev,
            benefits: [...(prev.benefits || []), response.data.data],
          }));
          toast.success(`Payment for ${type} benefit submitted for approval`);
        } catch (err) {
          toast.error("Payment verification or benefit creation failed");
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        toast.info("Payment cancelled");
        setLoading(false);
      },
    });
  };

  const calculateBenefits = (grossSalary) => ({
    essential: parseFloat(grossSalary || 0) * 0.1,
    health: parseFloat(grossSalary || 0) * 0.05,
  });

  const getTotalBenefits = () => {
    const totals = { essential: 0, health: 0, all: 0 };
    employee?.benefits?.forEach((benefit) => {
      if (benefit.benefitStatus === "Approved") {
        const amount = parseFloat(benefit.amountRequested);
        totals[benefit.benefitType] += amount;
        totals.all += amount;
      }
    });
    return totals;
  };

  if (status === "loading" && !employee) {
    return <div className="flex items-center justify-center h-screen bg-gray-100">Loading...</div>;
  }

  if (error && !employee) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        {typeof error === "string" ? error : "An error occurred while loading data"}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Benefits Management</h2>
      {employee ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-2xl font-semibold text-gray-700 mb-4">Salary and Benefits</h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-gray-50 border border-gray-200">
              <thead className="bg-orange-400 text-white">
                <tr>
                  <th className="py-3 px-4 text-left">Period</th>
                  <th className="py-3 px-4 text-left">Gross Salary (₦)</th>
                  <th className="py-3 px-4 text-left">Essential Benefit (10%)</th>
                  <th className="py-3 px-4 text-left">Health Benefit (5%)</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employee.salary?.history?.length > 0 ? (
                  employee.salary.history.map((hist, i) => {
                    const benefits = calculateBenefits(hist.grossSalary);
                    return (
                      <tr key={i} className="border-t hover:bg-gray-100">
                        <td className="py-3 px-4">{`${hist.month} ${hist.year}`}</td>
                        <td className="py-3 px-4">{(hist.grossSalary || 0).toLocaleString()}</td>
                        <td className="py-3 px-4">{benefits.essential.toLocaleString()}</td>
                        <td className="py-3 px-4">{benefits.health.toLocaleString()}</td>
                        <td className="py-3 px-4 flex gap-2">
                          <button
                            onClick={() => handlePayBenefit(benefits.essential, "essential", `${hist.month} ${hist.year}`)}
                            disabled={loading}
                            className={`px-4 py-2 rounded-lg text-white ${
                              loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"
                            }`}
                          >
                            Pay Essential
                          </button>
                          <button
                            onClick={() => handlePayBenefit(benefits.health, "health", `${hist.month} ${hist.year}`)}
                            disabled={loading}
                            className={`px-4 py-2 rounded-lg text-white ${
                              loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                            }`}
                          >
                            Pay Health
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
                      No salary history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 className="text-2xl font-semibold text-gray-700 mt-6 mb-4">Benefit Payment History</h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-gray-50 border border-gray-200">
              <thead className="bg-orange-400 text-white">
                <tr>
                  <th className="py-3 px-4 text-left">Type</th>
                  <th className="py-3 px-4 text-left">Amount (₦)</th>
                  <th className="py-3 px-4 text-left">Period</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Date Requested</th>
                </tr>
              </thead>
              <tbody>
                {employee.benefits?.length > 0 ? (
                  employee.benefits.map((benefit, i) => (
                    <tr key={i} className="border-t hover:bg-gray-100">
                      <td className="py-3 px-4 capitalize">{benefit.benefitType}</td>
                      <td className="py-3 px-4">{parseFloat(benefit.amountRequested).toLocaleString()}</td>
                      <td className="py-3 px-4">{benefit.benefitDescription.split(" for ")[1] || "N/A"}</td>
                      <td className="py-3 px-4">
                        <span
                          className={
                            benefit.benefitStatus === "Approved"
                              ? "text-green-600"
                              : benefit.benefitStatus === "Rejected"
                              ? "text-red-600"
                              : "text-yellow-600"
                          }
                        >
                          {benefit.benefitStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4">{new Date(benefit.createdAt).toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
                      No benefit payments made
                    </td>
                  </tr>              )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-lg font-semibold">
            <p>Total Essential Benefit Paid: ₦{getTotalBenefits().essential.toLocaleString()}</p>
            <p>Total Health Benefit Paid: ₦{getTotalBenefits().health.toLocaleString()}</p>
            <p>Total All Benefits Paid: ₦{getTotalBenefits().all.toLocaleString()}</p>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-center">No employee data available. Please contact your employer.</p>
      )}
    </div>
  );
};

export default JobSeekerBenefits;